import React from 'react';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Cell,
  LabelList,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Text,
} from 'recharts';
import { useData } from '../../contexts/useData';
import * as d3 from 'd3';
import { darken, lighten } from '@mui/material';

const customFill = (
  props,
  fieldKey,
  selectedGemeente,
  selectedBenchmarks,
  colors,
) => {
  const { value } = props;
  const out =
    parseInt(props[fieldKey]) === parseInt(selectedGemeente.id)
      ? colors[0]
      : parseInt(props[fieldKey]) === parseInt(selectedBenchmarks[0].id)
      ? colors[1]
      : colors[2];
  return out;
};

const renderCustomizedLabelHorizontal = (props) => {
  const { x, y, width, height, value } = props;
  const radius = 14;

  return (
    <g>
      <circle cx={x + width / 2} cy={y - radius} r={radius} fill="#1491BB" />
      <text
        x={x + width / 2}
        y={y - radius}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={'1.5rem'}
      >
        {value}
      </text>
    </g>
  );
};

const renderCustomizedLabelVertical = (props, colors) => {
  const { x, y, width, height, value } = props;
  const radius = 12;
  const yFix = 1;
  return (
    <g>
      <circle cx={x + width} cy={y} r={radius} fill={colors[0]} />
      <text
        x={x + width}
        y={y + yFix}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={'1.4rem'}
      >
        {value}
      </text>
    </g>
  );
};

const renderCustomizedLabelHorizontalBench = (props) => {
  const { x, y, width, height, value } = props;
  const radius = 10;

  return (
    <g>
      <circle cx={x + width / 2} cy={y - radius} r={radius} fill="#D5D5D5" />
      <text
        x={x + width / 2}
        y={y - radius}
        fill="#34444A"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="1.4rem"
      >
        {value}
      </text>
    </g>
  );
};

const renderCustomizedLabelVerticalBench = (props, colors) => {
  const { x, y, width, height, value } = props;
  const radius = 10;
  const yFix = 0;
  return (
    <g>
      <circle cx={x + width} cy={y + yFix} r={radius} fill={colors[1]} />
      <text
        x={x + width}
        y={y + yFix + 1}
        fill="#FFF"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="1.3rem"
      >
        {value}
      </text>
    </g>
  );
};

const renderLegend = (props, colors, interfaceColor) => {
  const { payload } = props;
  return (
    <div>
      <ul
        style={{
          padding: '0px',
          paddingLeft: '12rem',
          margin: '0px',
          textAlign: 'center',
          // marginTop: '40px',
        }}
      >
        {payload.toReversed().map((entry, index) => {
          // if (index !== payload.length - 1) {
          return (
            <li
              style={{
                fontSize: '1.4rem',
                display: 'inline-block',
                marginRight: '60px',
              }}
              key={`item-${index}`}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <path
                  // className={
                  //   index === 1
                  //     ? 'fill-highlight-benchmark'
                  //     : 'fill-highlight-gemeente'
                  // }
                  fill={index === 1 ? '#ddd' : interfaceColor}
                  cx="16"
                  cy="16"
                  transform="translate(16, 16)"
                  d="M16,0A16,16,0,1,1,-16,0A16,16,0,1,1,16,0"
                ></path>
              </svg>
              {entry.value}
            </li>
          );
          // }
        })}
      </ul>
    </div>
  );
};

const renderCustomizedLabel = (props) => {
  const { x, y, width, height, value } = props;
  const percentFormatter = d3.format(',.0%');
  return (
    <g>
      <text
        x={value < 0.1 ? x + width + 16 : x + width - 16}
        y={y + height / 2}
        fill={value < 0.1 ? '#000' : '#fff'}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={'1.4rem'}
      >
        {value !== null ? percentFormatter(value) : ''}
      </text>
    </g>
  );
};

const formatXAxis = (tickItem) => {
  const percentFormatter = d3.format(',.0%');
  return percentFormatter(tickItem);
};

export const BarChartVogelvlucht = (props) => {
  const {
    data,
    layout,
    showLegend,
    fieldKey,
    colors,
    interfaceColor,
    selectedGemeente,
    selectedBenchmarks,
    locale,
  } = props;

  // TODO: Legend toggle:     https://codesandbox.io/s/recharts-with-legend-toggle-dqlts?file=/src/BarGraph.js
  // TODO: Hover interaction: https://celiaongsl.medium.com/2-ways-to-hack-the-bar-chart-on-recharts-more-tricks-hacking-recharts-2-1387f21ecb72

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const bench = payload.find((d) => d.dataKey === 'valueBench');
      const gem = payload.find((d) => d.dataKey === 'value');

      const valueBench = bench ? locale.format(',.0%')(bench.value) : null;
      const value = gem ? locale.format(',.0%')(gem.value) : null;

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          {payload.length > 1 && (
            <p style={{ marginBottom: '2px' }}>
              <span
                style={{ fontWeight: 'bold' }}
              >{`${payload[1].name}:`}</span>{' '}
              {`${value}`}
            </p>
          )}
          <p>
            <span style={{ fontWeight: 'bold' }}>{`${payload[0].name}:`}</span>{' '}
            {`${valueBench}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={layout === 'vertical' ? layout : 'horizontal'}
        barGap={-12}
      >
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <XAxis
          type={'number'}
          hide={true}
          domain={[0, 'dataMax']}
          stroke="#34444A"
          tickCount={5}
          style={{ transform: 'translate(0,0)' }}
          fontSize={'1rem'}
          tickFormatter={formatXAxis}
        />

        <YAxis
          type="category"
          dataKey="label"
          interval={0}
          stroke="#333"
          strokeWidth={0}
          fontSize={'1.4rem'}
          width={250}
        />

        <Bar
          dataKey="valueBench"
          key={'_2' + fieldKey}
          barSize={24}
          name={selectedBenchmarks[0].label}
        >
          {data.map((d, i) => (
            <Cell fill={'#ddd'}></Cell>
          ))}
        </Bar>
        <Bar
          dataKey="value"
          key={'_1' + fieldKey}
          barSize={24}
          name={selectedGemeente.label}
        >
          <LabelList
            key={'_label' + fieldKey}
            dataKey="value"
            position="insideRight"
            fill="white"
            offset={14}
            content={renderCustomizedLabel}
          />

          {data.map((d, i) => (
            <Cell fill={colors[i]}></Cell>
          ))}
        </Bar>

        {showLegend && (
          <Legend
            content={(props) => renderLegend(props, colors, interfaceColor)}
            // formatter={(value, entry, index) => (
            //   <span style={{ color: 'blue' }} className="text-color-class">
            //     {value}
            //   </span>
            // )}
            iconType="circle"
          ></Legend>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

const renderLegendMobile = (props, interfaceColor) => {
  const { payload } = props;
  return (
    <ul
      style={{
        padding: '0px',
        paddingTop: '20px',
        paddingLeft: '2rem',
        margin: '0px',
        textAlign: 'center',
      }}
    >
      {payload.map((entry, index) => {
        // if (index !== payload.length - 1) {
        return (
          <li
            style={{
              fontSize: '11px',
              display: 'inline-block',
              marginRight: '20px',
            }}
            key={`item-${index}`}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 32 32"
              style={{
                display: 'inline-block',
                verticalAlign: 'middle',
                marginRight: '4px',
              }}
            >
              <title></title>
              <desc></desc>
              <path
                // className={
                //   index === 1
                //     ? 'fill-highlight-benchmark'
                //     : 'fill-highlight-gemeente'
                // }
                fill={index === 1 ? '#ddd' : interfaceColor}
                cx="16"
                cy="16"
                transform="translate(16, 16)"
                d="M16,0A16,16,0,1,1,-16,0A16,16,0,1,1,16,0"
              ></path>
            </svg>
            {entry.value}
          </li>
        );
        // }
      })}
    </ul>
  );
};

const renderCustomizedLabelMobile = (props) => {
  const { x, y, width, height, value } = props;
  const percentFormatter = d3.format(',.0%');
  return (
    <g>
      <text
        // x={value < 0.12 ? x + width + 12 : x + width - 14}
        // fill={value < 0.12 ? '#000' : '#fff'}
        x={x + width + 12}
        y={y + height / 2}
        fill={'#000'}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={'11px'}
      >
        {value !== null ? percentFormatter(value) : ''}
      </text>
    </g>
  );
};

export const BarChartVogelvluchtMobile = (props) => {
  const {
    data,
    layout,
    showLegend,
    fieldKey,
    colors,
    interfaceColor,
    selectedGemeente,
    selectedBenchmarks,
    locale,
  } = props;

  // TODO: Legend toggle:     https://codesandbox.io/s/recharts-with-legend-toggle-dqlts?file=/src/BarGraph.js
  // TODO: Hover interaction: https://celiaongsl.medium.com/2-ways-to-hack-the-bar-chart-on-recharts-more-tricks-hacking-recharts-2-1387f21ecb72
  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={140}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'11px'}
      >
        {payload.value}
      </Text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const bench = payload.find((d) => d.dataKey === 'valueBench');
      const gem = payload.find((d) => d.dataKey === 'value');

      const valueBench = bench ? locale.format(',.0%')(bench.value) : null;
      const value = gem ? locale.format(',.0%')(gem.value) : null;

      return (
        <div
          style={{
            fontSize: '11px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '200px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {gem.payload && gem.payload.label}
          </p>
          <p>{definitie}</p>

          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${
              gem.payload && gem.name
            }:`}</span>{' '}
            {`${value}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${
              bench.payload && bench.name
            }:`}</span>{' '}
            {`${valueBench}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        margin={{ top: 0, right: 60, bottom: 0, left: 40 }}
        data={data}
        layout={layout === 'vertical' ? layout : 'horizontal'}
        barGap={0}
      >
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <XAxis
          type={'number'}
          hide={true}
          domain={[0, 'dataMax']}
          stroke="#34444A"
          // tickCount={5}
          style={{ transform: 'translate(0,0)' }}
          fontSize={'11px'}
          tickFormatter={formatXAxis}
        />

        <YAxis
          type="category"
          dataKey="label"
          interval={0}
          stroke="#333"
          strokeWidth={0}
          fontSize={'11px'}
          width={100}
          tick={<CustomizedAxisTick />}
        />

        <Bar
          dataKey="value"
          key={'_1' + fieldKey}
          barSize={16}
          name={selectedGemeente.label}
        >
          <LabelList
            key={'_label' + fieldKey}
            dataKey="value"
            position="insideRight"
            fill="white"
            offset={10}
            content={renderCustomizedLabelMobile}
          />

          {data.map((d, i) => (
            <Cell fill={colors[i]}></Cell>
          ))}
        </Bar>
        <Bar
          dataKey="valueBench"
          key={'_2' + fieldKey}
          barSize={16}
          name={selectedBenchmarks[0].label}
        >
          <LabelList
            key={'_label2' + fieldKey}
            dataKey="valueBench"
            position="insideRight"
            fill="black"
            offset={10}
            content={renderCustomizedLabelMobile}
          />
          {data.map((d, i) => (
            <Cell fill={'#ddd'}></Cell>
          ))}
        </Bar>

        {showLegend && (
          <Legend
            content={(props) => renderLegendMobile(props, interfaceColor)}
            // formatter={(value, entry, index) => (
            //   <span style={{ color: 'blue' }} className="text-color-class">
            //     {value}
            //   </span>
            // )}
            iconType="circle"
          ></Legend>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartRegular = (props) => {
  const { data, layout, showLegend, fieldKey } = props;
  const { colors, selectedGemeente, selectedBenchmarks } = useData();

  // TODO: Legend toggle:     https://codesandbox.io/s/recharts-with-legend-toggle-dqlts?file=/src/BarGraph.js
  // TODO: Hover interaction: https://celiaongsl.medium.com/2-ways-to-hack-the-bar-chart-on-recharts-more-tricks-hacking-recharts-2-1387f21ecb72

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        margin={{ top: 6, left: 60, right: 120, bottom: 6 }}
        data={data}
        layout={layout === 'vertical' ? layout : 'horizontal'}
      >
        <Bar dataKey="value" key={fieldKey}>
          {data.map((d, index) => (
            <Cell
              key={fieldKey}
              fill={customFill(
                d,
                fieldKey,
                selectedGemeente,
                selectedBenchmarks,
                colors,
              )}
            />
          ))}
          <LabelList position={layout === 'vertical' ? 'right' : 'top'} />
        </Bar>

        {layout === 'vertical' ? (
          <XAxis
            type={'number'}
            // hide={true}
            domain={[0, (dataMax) => (dataMax * 1.1).toFixed(3)]}
            // domain={[0, (dataMax: any) => roundNumberUp(dataMax, 1)]}
            // unit="%"
          />
        ) : (
          <XAxis
            type="category"
            dataKey="label"
            interval={0}
            stroke={'#909090'}
          />
        )}
        {layout === 'vertical' ? (
          <YAxis
            type="category"
            dataKey="label"
            interval={0}
            stroke={'#909090'}
          />
        ) : (
          <YAxis
            type={'number'}
            // hide={true}
            domain={[0, (dataMax) => dataMax]}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartSurveyDots = (props) => {
  const {
    data,
    layout,
    showLegend,
    fieldKey,
    colors,
    selectedGemeente,
    selectedBenchmarks,
  } = props;
  // const { colors, selectedGemeente, selectedBenchmarks } = useData();

  // TODO: Legend toggle:     https://codesandbox.io/s/recharts-with-legend-toggle-dqlts?file=/src/BarGraph.js
  // TODO: Hover interaction: https://celiaongsl.medium.com/2-ways-to-hack-the-bar-chart-on-recharts-more-tricks-hacking-recharts-2-1387f21ecb72

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={layout === 'vertical' ? layout : 'horizontal'}
        barGap={-1}
      >
        {layout === 'vertical' ? (
          <XAxis
            type={'number'}
            // hide={true}
            domain={[0, 100]}
            stroke="#34444A"
            tickCount={2}
            style={{ transform: 'translate(0,0)' }}
            fontSize={'1rem'}
            unit="%"
          />
        ) : (
          <XAxis
            type="category"
            dataKey="id"
            interval={0}
            stroke="#34444A"
            fontSize={'1.3rem'}
          />
        )}
        {layout === 'vertical' ? (
          <YAxis
            type="category"
            dataKey="id"
            interval={0}
            stroke="#333"
            strokeWidth={0}
            fontSize={'1.4rem'}
            width={250}
          />
        ) : (
          <YAxis
            type={'number'}
            // hide={true}
            domain={[0, 100]}
            stroke="#333"
            tickCount={2}
            fontSize={'1rem'}
          />
        )}
        <Bar
          isAnimationActive={false}
          dataKey="valueMax"
          key={'_0' + fieldKey}
          barSize={1}
          fill={'#EDEDED'}
        ></Bar>
        {/* Enable to draw the bar for the main value */}
        {/* <Bar
          isAnimationActive={false}
          dataKey="value"
          key={'bar_' + fieldKey}
          barSize={1}
          fill={colors[0]}
        ></Bar> */}
        <Bar
          isAnimationActive={false}
          dataKey="valueBench"
          key={'_2' + fieldKey}
          barSize={1}
          fill="transparent"
          name={selectedBenchmarks[0].label}
        >
          {layout === 'vertical' ? (
            <LabelList
              dataKey="valueBench"
              content={(props) =>
                renderCustomizedLabelVerticalBench(props, colors)
              }
            />
          ) : (
            <LabelList
              dataKey="valueBench"
              content={renderCustomizedLabelHorizontalBench}
            />
          )}
        </Bar>
        <Bar
          isAnimationActive={false}
          dataKey="value"
          key={'_1' + fieldKey}
          barSize={1}
          fill="transparent"
          name={selectedGemeente.label}
        >
          {layout === 'vertical' ? (
            <LabelList
              key={'_label' + fieldKey}
              dataKey="value"
              content={(props) => renderCustomizedLabelVertical(props, colors)}
            />
          ) : (
            <LabelList
              dataKey="value"
              content={renderCustomizedLabelHorizontal}
            />
          )}
        </Bar>
        {showLegend && (
          <Legend
            content={(props) => renderLegend(props, colors)}
            // formatter={(value, entry, index) => (
            //   <span style={{ color: 'blue' }} className="text-color-class">
            //     {value}
            //   </span>
            // )}
            iconType="circle"
          ></Legend>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartAnimated = (props) => {
  const { data, layout, showLegend, fieldKey, fill } = props;
  const { colors, selectedGemeente, selectedBenchmarks } = useData();

  // TODO: Legend toggle:     https://codesandbox.io/s/recharts-with-legend-toggle-dqlts?file=/src/BarGraph.js
  // TODO: Hover interaction: https://celiaongsl.medium.com/2-ways-to-hack-the-bar-chart-on-recharts-more-tricks-hacking-recharts-2-1387f21ecb72

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        margin={{ top: 6, left: 60, right: 120, bottom: 6 }}
        data={data}
        layout={layout === 'vertical' ? layout : 'horizontal'}
        key={`animated_bar`}
      >
        <Bar dataKey="value" key="id" animationEasing="ease-in-out">
          {data.map((d, index) => (
            <Cell key={fieldKey} fill={d.fill} />
          ))}
          {/* <LabelList position={layout === 'vertical' ? 'right' : 'top'} /> */}
        </Bar>

        {layout === 'vertical' ? (
          <XAxis
            type={'number'}
            hide={true}
            domain={[0, 100]}
            // domain={[0, (dataMax: any) => roundNumberUp(dataMax, 1)]}
            // unit="%"
          />
        ) : (
          <XAxis
            type="category"
            dataKey="label"
            interval={0}
            stroke={'#909090'}
            hide={true}
          />
        )}
        {layout === 'vertical' ? (
          <YAxis
            type="category"
            dataKey="label"
            interval={0}
            stroke={'#909090'}
            hide={true}
          />
        ) : (
          <YAxis
            type={'number'}
            // hide={true}
            domain={[0, (dataMax) => dataMax]}
            hide={true}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartStacked = (props) => {
  // <BarChartStacked data={data} keysInStack={['Ja', 'Nee']} stacks={['Antwerpen', 'Bornem']} colors={['#000', '#1f1', ...]} />
  const { data, stacks, keysInStack, colors, locale } = props;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={130}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'0.8em'}
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        barGap={20}
        margin={{
          top: 0,
          right: 48,
          left: 96,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '0.8em' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
          domain={[0, 1]}
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          tick={<CustomizedAxisTick />}
        />
        <Tooltip
          contentStyle={{ fontSize: '15px', paddingBottom: '8px' }}
          itemStyle={{
            marginBottom: '0px',
            paddingBottom: '0px',
            marginTop: '0px',
            paddingTop: '0px',
          }}
          labelStyle={{
            fontWeight: 'bold',
            paddingBottom: '4px',
            marginBottom: '0px',
          }}
          cursor={{ fill: 'transparent' }}
          formatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        <Legend
          verticalAlign="top"
          height={keysInStack.length > 3 ? 90 : 52}
          wrapperStyle={{ fontSize: '14px' }}
        />
        {keysInStack.map((d, i) => {
          return (
            <Bar dataKey={d} fill={colors[i]} barSize={24} stackId="a">
              <LabelList
                dataKey={d}
                position="inside"
                style={{ fontSize: '15px' }}
                formatter={(value) =>
                  data[0] && data[0].d3format
                    ? locale.format(data[0].d3format)(value)
                    : value
                }
                fill={'#111'}
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartSimple = (props) => {
  const { data, keysInStack, colors, locale, legend, survey } = props;

  let showLegend = legend === undefined ? false : legend;
  let isSurvey = survey === undefined ? false : survey;

  const renderLabel = (props) => {
    const { x, y, width, height, value, color } = props;

    return (
      <g>
        <text
          x={x + width + 4}
          y={y + height / 2 + 1}
          fill={value < 0 ? '#fff' : color}
          textAnchor="start"
          dominantBaseline="middle"
          fontSize={'1.4rem'}
        >
          {value !== null
            ? data[0] && data[0].d3format && locale !== undefined
              ? locale.format(data[0].d3format)(value)
              : value
            : ''}
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value =
        data[0] && data[0].d3format && locale !== undefined
          ? locale.format(data[0].d3format)(payload[0].value)
          : value;

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p>
            <span
              style={{ fontWeight: 'bold' }}
            >{`${payload[0].payload.name}:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={170}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'0.8em'}
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 100,
          bottom: 0,
        }}
      >
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '0.8em' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
          domain={isSurvey ? [0, 1] : [0, 'auto']}
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          // tick={{ fontSize: '0.8em' }}
          tick={<CustomizedAxisTick />}
        />
        <Tooltip
          labelStyle={{
            color: 'black',
            fontSize: '16px',
            marginBottom: '8px',
          }}
          contentStyle={{
            fontSize: '14px',
            color: '#34444A',
            paddingBottom: '4px',
          }}
          cursor={{ fill: 'transparent' }}
          formatter={(value) =>
            data[0] && data[0].d3format && locale !== undefined
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />

        {showLegend && <Legend verticalAlign="top" height={36} />}

        <Bar dataKey={keysInStack[0]} stackId="a" fill={colors[0]} barSize={15}>
          {data.map((d, i) => (
            <Cell fill={d.color}></Cell>
          ))}
          <LabelList
            dataKey={keysInStack[0]}
            position={'right'}
            style={{ fontSize: '15px' }}
            formatter={(value) =>
              data[0] && data[0].d3format
                ? locale.format(data[0].d3format)(value)
                : value
            }
            content={renderLabel}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartStackedSplit = (props) => {
  // <BarChartStacked data={data} keysInStack={['Ja', 'Nee']} stacks={['Antwerpen', 'Bornem']} colors={['#000', '#1f1', ...]} />
  const { data, stacks, keysInStack, colors, locale, legend } = props;

  const showLegend = legend === undefined ? true : legend;
  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={200}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'0.8em'}
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 96,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '0.8em' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          width={120}
          tick={<CustomizedAxisTick />}
        />
        <Tooltip
          contentStyle={{ fontSize: '15px', paddingBottom: '8px' }}
          itemStyle={{
            marginBottom: '0px',
            paddingBottom: '0px',
            marginTop: '0px',
            paddingTop: '0px',
          }}
          labelStyle={{
            fontWeight: 'bold',
            paddingBottom: '4px',
            marginBottom: '0px',
          }}
          cursor={{ fill: 'transparent' }}
          formatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        {showLegend && (
          <Legend
            verticalAlign="top"
            height={keysInStack.length > 3 ? 90 : 52}
            wrapperStyle={{ fontSize: '14px' }}
          />
        )}
        {keysInStack.map((d, i) => {
          return (
            <Bar dataKey={d} fill={colors[i]} barSize={12}>
              <LabelList
                dataKey={d}
                position="right"
                style={{ fontSize: '15px' }}
                formatter={(value) =>
                  data[0] && data[0].d3format
                    ? locale.format(data[0].d3format)(value)
                    : value
                }
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartIndex = (props) => {
  const { data, colors, locale } = props;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload.detail
        ? payload[0].payload.detail.definitie
        : '(ontbrekende definitie)';

      const value = locale.format(',.1f')(payload[0].payload.indexScore);

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Indexscore:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 54,
          right: 64,
          left: 16,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '14px' }}
          domain={[-100, 100]}
          tickFormatter={(value) => locale.format(',.1f')(value + 100)}
        />
        <YAxis
          type="category"
          dataKey="label"
          axisLine={false}
          width={260}
          tick={{ fontSize: '14px' }}
        />
        {/* <Tooltip
          labelStyle={{
            color: 'black',
            fontSize: '16px',
            marginBottom: '8px',
          }}
          contentStyle={{
            fontSize: '14px',
            color: '#34444A',
            paddingBottom: '4px',
          }}
          cursor={{ fill: 'transparent' }}

        /> */}

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <Bar dataKey={'indexScoreVisual'} stackId="a" barSize={12}>
          {data.map((d, i) => {
            const color = d.indexScore < 100 ? '#2B92BE' : '#D53E5E';
            return <Cell fill={color}></Cell>;
          })}
          <LabelList
            dataKey={'indexScoreVisual'}
            position="right"
            formatter={(value) => locale.format(',.1f')(value + 100)}
            fontSize="14px"
          />
        </Bar>
        <ReferenceLine x={0} stroke={'black'} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartIndexMobile = (props) => {
  const { data, colors, locale } = props;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload.detail
        ? payload[0].payload.detail.definitie
        : '(ontbrekende definitie)';

      const value = locale.format(',.1f')(payload[0].payload.indexScore);

      return (
        <div
          style={{
            fontSize: '11px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '200px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Indexscore:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 32,
          right: 48,
          left: 32,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '11px' }}
          domain={[-100, 100]}
          tickFormatter={(value) => locale.format(',.1f')(value + 100)}
        />
        <YAxis
          type="category"
          dataKey="label"
          axisLine={false}
          width={120}
          tick={{ fontSize: '11px' }}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <Bar dataKey={'indexScoreVisual'} stackId="a" barSize={12}>
          {data.map((d, i) => {
            const color = d.indexScore < 100 ? '#2B92BE' : '#D53E5E';

            return <Cell fill={color}></Cell>;
          })}
          <LabelList
            dataKey={'indexScoreVisual'}
            fontSize={'11px'}
            position="right"
            formatter={(value) => locale.format(',.1f')(value + 100)}
          />
        </Bar>
        <ReferenceLine x={0} stroke={'black'} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartSimpleMobile = (props) => {
  const { data, keysInStack, colors, locale, legend, survey } = props;

  let showLegend = legend === undefined ? false : legend;
  let isSurvey = survey === undefined ? false : survey;

  const renderLabel = (props) => {
    const { x, y, width, height, value, color } = props;

    return (
      <g>
        <text
          x={x + width + 4}
          y={y + height / 2 + 1}
          fill={value < 0 ? '#fff' : color}
          textAnchor="start"
          dominantBaseline="middle"
          fontSize={'11px'}
        >
          {value !== null
            ? data[0] && data[0].d3format && locale !== undefined
              ? locale.format(data[0].d3format)(value)
              : value
            : ''}
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value =
        data[0] && data[0].d3format && locale !== undefined
          ? locale.format(data[0].d3format)(payload[0].value)
          : value;

      return (
        <div
          style={{
            fontSize: '11px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p>
            <span
              style={{ fontWeight: 'bold' }}
            >{`${payload[0].payload.name}:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={120}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'11px'}
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 48,
          bottom: 0,
        }}
      >
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '11px' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
          domain={isSurvey ? [0, 1] : [0, 'auto']}
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          // tick={{ fontSize: '0.8em' }}
          tick={<CustomizedAxisTick />}
        />
        <Tooltip
          labelStyle={{
            color: 'black',
            fontSize: '16px',
            marginBottom: '8px',
          }}
          contentStyle={{
            fontSize: '14px',
            color: '#34444A',
            paddingBottom: '4px',
          }}
          cursor={{ fill: 'transparent' }}
          formatter={(value) =>
            data[0] && data[0].d3format && locale !== undefined
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />

        {showLegend && <Legend verticalAlign="top" height={36} />}

        <Bar dataKey={keysInStack[0]} stackId="a" fill={colors[0]} barSize={15}>
          {data.map((d, i) => (
            <Cell fill={d.color}></Cell>
          ))}
          <LabelList
            dataKey={keysInStack[0]}
            position={'right'}
            style={{ fontSize: '11px' }}
            formatter={(value) =>
              data[0] && data[0].d3format
                ? locale.format(data[0].d3format)(value)
                : value
            }
            content={renderLabel}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartStackedMobile = (props) => {
  // <BarChartStacked data={data} keysInStack={['Ja', 'Nee']} stacks={['Antwerpen', 'Bornem']} colors={['#000', '#1f1', ...]} />
  const { data, stacks, keysInStack, colors, locale } = props;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={100}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'11px'}
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        barGap={20}
        margin={{
          top: 0,
          right: 48,
          left: 40,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '0.8em' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
          domain={[0, 1]}
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          tick={<CustomizedAxisTick />}
        />
        <Tooltip
          contentStyle={{ fontSize: '11px', paddingBottom: '8px' }}
          itemStyle={{
            marginBottom: '0px',
            paddingBottom: '0px',
            marginTop: '0px',
            paddingTop: '0px',
          }}
          labelStyle={{
            fontWeight: 'bold',
            paddingBottom: '4px',
            marginBottom: '0px',
          }}
          cursor={{ fill: 'transparent' }}
          formatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        <Legend
          verticalAlign="top"
          height={
            keysInStack.length > 9
              ? 200
              : keysInStack.length > 6
              ? 140
              : keysInStack.length > 3
              ? 90
              : 48
          }
          wrapperStyle={{ fontSize: '11px' }}
        />
        {keysInStack.map((d, i) => {
          return (
            <Bar dataKey={d} fill={colors[i]} barSize={18} stackId="a">
              <LabelList
                dataKey={d}
                position="inside"
                style={{ fontSize: '11px' }}
                formatter={(value) =>
                  data[0] && data[0].d3format
                    ? locale.format(data[0].d3format)(value)
                    : value
                }
                fill={'#111'}
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartStackedSplitMobile = (props) => {
  // <BarChartStacked data={data} keysInStack={['Ja', 'Nee']} stacks={['Antwerpen', 'Bornem']} colors={['#000', '#1f1', ...]} />
  const { data, stacks, keysInStack, colors, locale } = props;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={100}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'11px'}
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 32,
          left: 0,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '11px' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          width={80}
          tick={<CustomizedAxisTick />}
        />
        <Tooltip
          contentStyle={{ fontSize: '11px', paddingBottom: '8px' }}
          itemStyle={{
            marginBottom: '0px',
            paddingBottom: '0px',
            marginTop: '0px',
            paddingTop: '0px',
          }}
          labelStyle={{
            fontWeight: 'bold',
            paddingBottom: '4px',
            marginBottom: '0px',
          }}
          cursor={{ fill: 'transparent' }}
          formatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        <Legend
          verticalAlign="top"
          height={
            keysInStack.length > 9
              ? 200
              : keysInStack.length > 6
              ? 140
              : keysInStack.length > 3
              ? 90
              : 48
          }
          wrapperStyle={{ fontSize: '11px' }}
        />
        {keysInStack.map((d, i) => {
          return (
            <Bar dataKey={d} fill={colors[i]} barSize={12}>
              <LabelList
                dataKey={d}
                position="right"
                style={{ fontSize: '11px' }}
                formatter={(value) =>
                  data[0] && data[0].d3format
                    ? locale.format(data[0].d3format)(value)
                    : value
                }
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartDeltaRel = (props) => {
  const { data, colors, locale } = props;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const value =
        payload[0].payload.delta > 0
          ? '+' + locale.format('.1%')(payload[0].payload.delta)
          : locale.format('.1%')(payload[0].payload.delta);

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Relatief verschil:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 16,
          left: 16,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '14px' }}
          domain={([dataMin, dataMax]) => {
            let absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
            absMax = absMax < 0.1 ? 0.1 : absMax;
            return [(-absMax * 1.4).toFixed(1), (absMax * 1.4).toFixed(1)];
          }}
          // domain={[-1, 1]}
          interval="equidistantStartEnd"
          tickCount={5}
          interval={0}
          tickFormatter={(delta) => locale.format('.0%')(delta)}
        />
        <YAxis
          type="category"
          dataKey="label"
          axisLine={false}
          width={0}
          tick={{ fontSize: '14px' }}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <Bar dataKey={'delta'} stackId="a" barSize={12}>
          {data.map((d, i) => {
            const color = d.delta < 0 ? colors[1] : colors[0];
            return <Cell fill={color}></Cell>;
          })}
          <LabelList
            dataKey={'delta'}
            position="right"
            formatter={(value) =>
              value > 0
                ? '+' + locale.format('.1%')(value)
                : locale.format('.1%')(value)
            }
            fontSize="14px"
          />
        </Bar>
        <ReferenceLine x={0} stroke={'black'} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartDelta = (props) => {
  const { data, colors, locale } = props;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const value =
        payload[0].payload.deltaAbs > 0
          ? '+' + locale.format('.0%')(payload[0].payload.deltaAbs)
          : locale.format('.0%')(payload[0].payload.deltaAbs);

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Verschil:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 16,
          left: 16,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '14px' }}
          domain={([dataMin, dataMax]) => {
            let absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
            absMax = absMax < 0.1 ? 0.1 : absMax;
            return [(-absMax * 1.5).toFixed(1), (absMax * 1.5).toFixed(1)];
          }}
          // domain={[-1, 1]}
          interval="equidistantStartEnd"
          tickCount={5}
          interval={0}
          tickFormatter={(delta) => locale.format('.0%')(delta)}
        />
        <YAxis
          type="category"
          dataKey="label"
          axisLine={false}
          width={0}
          tick={{ fontSize: '14px' }}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <Bar dataKey={'deltaAbs'} stackId="a" barSize={12}>
          {data.map((d, i) => {
            const color = d.deltaAbs < 0 ? colors[1] : colors[0];
            return <Cell fill={color}></Cell>;
          })}
          <LabelList
            dataKey={'deltaAbs'}
            position="right"
            formatter={(value) =>
              value > 0
                ? '+' + locale.format('.0%')(value)
                : locale.format('.0%')(value)
            }
            fontSize="14px"
          />
        </Bar>
        <ReferenceLine x={0} stroke={'black'} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartComparisson = (props) => {
  // <BarChartStacked data={data} keysInStack={['Ja', 'Nee']} stacks={['Antwerpen', 'Bornem']} colors={['#000', '#1f1', ...]} />
  const { data, labels, keysInStack, colors, locale, legend } = props;

  const showLegend = legend === undefined ? true : legend;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={300}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'14px'}
      >
        {payload.value}
      </Text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const value = locale.format(payload[0].payload.d3format)(
        payload[0].payload[keysInStack[0]],
      );
      const valueVorig = locale.format(payload[0].payload.d3format)(
        payload[0].payload[keysInStack[1]],
      );
      const delta =
        payload[0].payload.deltaAbs > 0
          ? '+' + locale.format('.0%')(payload[0].payload.deltaAbs)
          : locale.format('.0%')(payload[0].payload.deltaAbs);
      const deltaRel =
        payload[0].payload.delta > 0
          ? '+' + locale.format('.1%')(payload[0].payload.delta)
          : locale.format('.1%')(payload[0].payload.delta);

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${labels[0]}:`}</span>{' '}
            {`${value}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${labels[1]}:`}</span>{' '}
            {`${valueVorig}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Verschil:`}</span>{' '}
            {`${delta}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Relatief verschil:`}</span>{' '}
            {`${deltaRel}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 140,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '14px' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          width={140}
          tick={<CustomizedAxisTick />}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        {showLegend && (
          <Legend
            verticalAlign="top"
            height={keysInStack.length > 3 ? 90 : 52}
            wrapperStyle={{ fontSize: '14px' }}
          />
        )}
        {keysInStack.map((d, i) => {
          return (
            <Bar dataKey={d} fill={colors[i]} barSize={i === 0 ? 12 : 6}>
              <LabelList
                dataKey={d}
                position="right"
                style={{ fontSize: '14px' }}
                formatter={(value) =>
                  data[0] && data[0].d3format
                    ? locale.format(data[0].d3format)(value)
                    : value
                }
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartComparissonSimple = (props) => {
  // <BarChartStacked data={data} keysInStack={['Ja', 'Nee']} stacks={['Antwerpen', 'Bornem']} colors={['#000', '#1f1', ...]} />
  const { data, labels, keysInStack, colors, locale, legend } = props;

  const showLegend = legend === undefined ? true : legend;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={300}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'14px'}
      >
        {payload.value}
      </Text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const value = locale.format(payload[0].payload.d3format)(
        payload[0].payload[keysInStack[0]],
      );
      const valueVorig = locale.format(payload[0].payload.d3format)(
        payload[0].payload[keysInStack[1]],
      );
      const delta = locale.format(payload[0].payload.d3format)(
        payload[0].payload.deltaAbs,
      );
      const deltaRel = locale.format(payload[0].payload.d3format)(
        payload[0].payload.delta,
      );

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${labels[0]}:`}</span>{' '}
            {`${value}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${labels[1]}:`}</span>{' '}
            {`${valueVorig}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Verschil:`}</span>{' '}
            {`${delta}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Relatief verschil:`}</span>{' '}
            {`${deltaRel}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 140,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '14px' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
          hide={true}
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          width={140}
          tick={<CustomizedAxisTick />}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        {showLegend && (
          <Legend
            verticalAlign="top"
            height={keysInStack.length > 3 ? 90 : 52}
            wrapperStyle={{ fontSize: '14px' }}
          />
        )}
        {keysInStack.map((d, i) => {
          return (
            <Bar dataKey={d} fill={colors[i]} barSize={i === 0 ? 12 : 6}>
              <LabelList
                dataKey={d}
                position="right"
                style={{ fontSize: '14px' }}
                formatter={(value) =>
                  data[0] && data[0].d3format
                    ? locale.format(data[0].d3format)(value)
                    : value
                }
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartComparissonMobile = (props) => {
  // <BarChartStacked data={data} keysInStack={['Ja', 'Nee']} stacks={['Antwerpen', 'Bornem']} colors={['#000', '#1f1', ...]} />
  const { data, labels, keysInStack, colors, locale, legend } = props;

  const showLegend = legend === undefined ? true : legend;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={140}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'11px'}
      >
        {payload.value}
      </Text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const value = locale.format(payload[0].payload.d3format)(
        payload[0].payload[keysInStack[0]],
      );
      const valueVorig = locale.format(payload[0].payload.d3format)(
        payload[0].payload[keysInStack[1]],
      );
      const delta =
        payload[0].payload.deltaAbs > 0
          ? '+' + locale.format('.0%')(payload[0].payload.deltaAbs)
          : locale.format('.0%')(payload[0].payload.deltaAbs);
      const deltaRel =
        payload[0].payload.delta > 0
          ? '+' + locale.format('.1%')(payload[0].payload.delta)
          : locale.format('.1%')(payload[0].payload.delta);

      return (
        <div
          style={{
            fontSize: '11px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '200px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${labels[0]}:`}</span>{' '}
            {`${value}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`${labels[1]}:`}</span>{' '}
            {`${valueVorig}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Verschil:`}</span>{' '}
            {`${delta}`}
          </p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Relatief verschil:`}</span>{' '}
            {`${deltaRel}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 40,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '11px' }}
          tickFormatter={(value) =>
            data[0] && data[0].d3format
              ? locale.format(data[0].d3format)(value)
              : value
          }
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          width={100}
          tick={<CustomizedAxisTick />}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        {showLegend && (
          <Legend
            verticalAlign="top"
            height={keysInStack.length > 3 ? 90 : 52}
            wrapperStyle={{ fontSize: '11px' }}
          />
        )}
        {keysInStack.map((d, i) => {
          return (
            <Bar dataKey={d} fill={colors[i]} barSize={i === 0 ? 12 : 6}>
              <LabelList
                dataKey={d}
                position="right"
                style={{ fontSize: '11px' }}
                formatter={(value) =>
                  data[0] && data[0].d3format
                    ? locale.format(data[0].d3format)(value)
                    : value
                }
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartDeltaRelMobile = (props) => {
  const { data, colors, locale } = props;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={140}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'11px'}
      >
        {payload.value}
      </Text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const value =
        payload[0].payload.delta > 0
          ? '+' + locale.format('.1%')(payload[0].payload.delta)
          : locale.format('.1%')(payload[0].payload.delta);

      return (
        <div
          style={{
            fontSize: '11px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '200px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Relatief verschil:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 40,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '11px' }}
          domain={([dataMin, dataMax]) => {
            let absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
            absMax = absMax < 0.1 ? 0.1 : absMax;
            return [(-absMax * 1.4).toFixed(1), (absMax * 1.4).toFixed(1)];
          }}
          // domain={[-1, 1]}
          interval="equidistantStartEnd"
          tickCount={5}
          interval={0}
          tickFormatter={(delta) => locale.format('.0%')(delta)}
        />
        <YAxis
          type="category"
          dataKey="label"
          axisLine={false}
          width={100}
          tick={<CustomizedAxisTick />}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <Bar dataKey={'delta'} stackId="a" barSize={12}>
          {data.map((d, i) => {
            const color = d.delta < 0 ? colors[1] : colors[0];
            return <Cell fill={color}></Cell>;
          })}
          <LabelList
            dataKey={'delta'}
            position="right"
            formatter={(value) =>
              value > 0
                ? '+' + locale.format('.1%')(value)
                : locale.format('.1%')(value)
            }
            fontSize="11px"
          />
        </Bar>
        <ReferenceLine x={0} stroke={'black'} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const BarChartDeltaMobile = (props) => {
  const { data, colors, locale } = props;

  const CustomizedAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <Text
        x={x}
        y={y}
        width={140}
        textAnchor="end"
        verticalAnchor="middle"
        fontSize={'11px'}
      >
        {payload.value}
      </Text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const definitie = payload[0].payload
        ? payload[0].payload.definitie
        : '(ontbrekende definitie)';

      const value =
        payload[0].payload.deltaAbs > 0
          ? '+' + locale.format('.0%')(payload[0].payload.deltaAbs)
          : locale.format('.0%')(payload[0].payload.deltaAbs);

      return (
        <div
          style={{
            fontSize: '11px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '200px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {payload[0].payload.label}
          </p>
          <p>{definitie}</p>
          <p style={{ marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>{`Verschil:`}</span>{' '}
            {`${value}`}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        // key={Math.random()}
        data={data}
        layout="vertical"
        margin={{
          top: 0,
          right: 48,
          left: 40,
          bottom: 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          fill={'white'}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: '11px' }}
          domain={([dataMin, dataMax]) => {
            let absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
            absMax = absMax < 0.1 ? 0.1 : absMax;
            return [(-absMax * 1.5).toFixed(1), (absMax * 1.5).toFixed(1)];
          }}
          // domain={[-1, 1]}
          interval="equidistantStartEnd"
          tickCount={5}
          interval={0}
          tickFormatter={(delta) => locale.format('.0%')(delta)}
        />
        <YAxis
          type="category"
          dataKey="label"
          axisLine={false}
          width={100}
          tick={<CustomizedAxisTick />}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />

        <Bar dataKey={'deltaAbs'} stackId="a" barSize={12}>
          {data.map((d, i) => {
            const color = d.deltaAbs < 0 ? colors[1] : colors[0];
            return <Cell fill={color}></Cell>;
          })}
          <LabelList
            dataKey={'deltaAbs'}
            position="right"
            formatter={(value) =>
              value > 0
                ? '+' + locale.format('.0%')(value)
                : locale.format('.0%')(value)
            }
            fontSize="11px"
          />
        </Bar>
        <ReferenceLine x={0} stroke={'black'} />
      </BarChart>
    </ResponsiveContainer>
  );
};
